

interface MetalResponse {
    status: string;
    currency: string;
    unit: string;
    metals: {
        [key: string]: number;
    };
    timestamps: {
        metal: string;
        currency: string;
    };
}

export interface CachedMetalPrices {
    copper: number;
    aluminum: number;
    lastUpdated: number; // Timestamp
}

const CACHE_KEY = 'electro_cable_metal_prices';
// 1 hour in milliseconds - much shorter cache since server handles global caching
const CACHE_DURATION = 60 * 60 * 1000;

export const getMetalPrices = async (): Promise<CachedMetalPrices | null> => {
    try {
        // 1. Check Local Cache first
        const cachedData = localStorage.getItem(CACHE_KEY);

        if (cachedData) {
            const parsedData: CachedMetalPrices = JSON.parse(cachedData);
            const now = Date.now();

            // If cache is fresh, return it
            if (now - parsedData.lastUpdated < CACHE_DURATION) {
                return parsedData;
            }
        }

        // 2. Fetch from our Proxy (Cloudflare Worker)
        // No API key needed here, it's in the backend
        const response = await fetch('/api/metal-prices', {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            }
        });

        if (!response.ok) {
            console.error(`HTTP error! status: ${response.status}`);
            return cachedData ? JSON.parse(cachedData) : null;
        }

        const data: MetalResponse & { serverTimestamp?: number } = await response.json();

        if (data.status === 'success') {
            const metals = data.metals;

            // Use server timestamp if available, otherwise current time
            const lastUpdated = data.serverTimestamp || Date.now();

            const newData: CachedMetalPrices = {
                copper: metals.copper || 0,
                aluminum: metals.aluminum || 0,
                lastUpdated: lastUpdated
            };

            // 3. Save to Cache
            localStorage.setItem(CACHE_KEY, JSON.stringify(newData));
            return newData;
        } else {
            console.error('Failed to fetch metal prices:', data);
            return cachedData ? JSON.parse(cachedData) : null;
        }

    } catch (error) {
        console.error('Error in metal price service:', error);
        // Fallback to cache if request fails
        const cachedData = localStorage.getItem(CACHE_KEY);
        return cachedData ? JSON.parse(cachedData) : null;
    }
};

export const clearMetalCache = () => {
    localStorage.removeItem(CACHE_KEY);
};
